import {
  FC, ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react'
import { useField, useFormikContext } from 'formik'
import cn from 'classnames'
import { IMaskInput } from 'react-imask'

import style from '@src/components/FieldFormik/FieldFormik.module.scss'
import { ReactComponent as Eye } from '@src/assets/eye.svg'
import { ReactComponent as Pencil } from '@src/assets/pencil.svg'
import { ReactComponent as Done } from '@src/assets/done.svg'
import { useDispatch, useSelector } from 'react-redux'
import { setField } from '@src/store/ducks/user/thunks'
import { AppDispatch } from '@src/store/store'
import { selectProfile } from '@src/store/ducks/user/selectors'
import moment from 'moment'

type FieldFormikProps = {
  name: string
  type?: string
  placeholder?: string
  label?: string | ReactNode
  disabled?: boolean
  className?: string
  mask?: string
  autocomplete?: 'off' | 'on'
  icon?: ReactNode
  outline?: boolean
  isEdit?: boolean
  saveField?: (value: string) => void
  pattern?: string
  styleError?: string
  onKeyPressBtn?: any
}

export const FieldFormik: FC<FieldFormikProps> = ({
  pattern, styleError, onKeyPressBtn,
  autocomplete = 'on', icon, disabled, className, name, type = 'text', label, placeholder, mask = '', outline, isEdit,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [field, meta] = useField(name)
  const { setFieldValue, setFieldError, setFieldTouched } = useFormikContext<any>()
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [show, setShow] = useState(false)
  const [isEditStatus, setIsEditStatus] = useState(false)
  const profile = useSelector(selectProfile)

  const isError = useMemo(() => !isFocused && meta.error && meta.touched, [isFocused, meta.error, meta.touched])
  useEffect(() => {
  }, [field])
  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])
  const handleBlur = useCallback((e) => {
    setIsFocused(false)
    field.onBlur(e)
  }, [field])

  const handleEditStatus = useCallback(() => {
    if (name === 'birthday' && profile?.lastBirthdayUpdate && moment(profile.lastBirthdayUpdate).add(1, 'year').isAfter(moment())) {
      setFieldTouched(name, true)
    } else {
      setIsEditStatus(true)
    }
  }, [name, profile, setFieldError, setFieldTouched])

  const handleSaveField = useCallback(() => {
    dispatch(setField(field.name, field.value))
    setIsEditStatus(false)
  }, [dispatch, field.name, field.value])

  return (
    <div className={cn(style.wrapper, className)}>
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <label>
        {label && <div className={style.label}>{label}</div>}
        {icon || ''}
        {mask ? (
          <IMaskInput
            // @ts-ignore
            onAccept={(e:any) => setFieldValue(name, e)}
            className={cn(style.input, outline && style.input_outline, isError && style.input_error)}
            type={type}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            name={name}
            mask={mask}
            value={field.value}
            disabled={disabled || (isEdit && !isEditStatus)}
            onKeyDown={onKeyPressBtn}
          />
        ) : (
          <>
            <input
              {...field}
              className={cn(style.input, outline && style.input_outline, isError && style.input_error)}
              type={!show ? type : 'text'}
              placeholder={placeholder}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled || (isEdit && !isEditStatus)}
              autoComplete={autocomplete}
              pattern={pattern}
              onKeyDown={onKeyPressBtn}
            />
            {type === 'password'
              ? (
                <button
                  className={cn(style.iconBox, show && style.active)}
                  type="button"
                  onClick={() => {
                    setShow(!show)
                  }}
                >
                  <Eye />
                </button>
              )
              : ''}
          </>
        )}
        {isError && <div className={cn(style.error, styleError)} dangerouslySetInnerHTML={{ __html: meta.error || '' }} />}
        {isEdit && (
          <>
            {isEditStatus ? (
              <button
                className={style.btnEdit}
                type="button"
                onClick={handleSaveField}
                aria-label="Сохранить"
                disabled={!!isError}
              >
                <Done />
              </button>
            ) : (
              <button
                className={style.btnEdit}
                type="button"
                onClick={handleEditStatus}
                aria-label="Редактировать"
              >
                <Pencil />
              </button>
            )}
          </>
        )}
      </label>
    </div>
  )
}
